// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import React from "react";
import { LazyQueryHookOptions } from "@apollo/client";
import { IndexForms } from "@hex-insights/app-modules";
import { ClassNameProps, Column, makeClassName, StyleProps } from "@hex-insights/core";
import {
	FieldDisplayArgs,
	FormValues,
	MultiSelectField,
	MultiSelectFieldProps,
	SelectField,
	SelectFieldProps,
} from "@hex-insights/forms";
import {
	Permission,
	PermissionSelectQuery,
	PermissionSelectQueryVariables,
	usePermissionSelectLazyQuery,
} from "../../../Utilities";
import { PermissionSelect } from "../../FormUtilities";
import { PermissionLink } from "../../Links";
import { PermissionOrderFormValues } from "../../Order";
import styles from "./select.module.css";

export type SelectProps<FV extends FormValues> = Omit<
	SelectFieldProps<FV, Permission["id"] | null>,
	"options" | "isLoading" | "blankValue" | "className" | "style"
> & {
	currentInstance?: PermissionSelect.ModelForOption | null;
	queryOptions?: LazyQueryHookOptions<PermissionSelectQuery, PermissionSelectQueryVariables>;
	load?: boolean;
	fieldClassName?: string;
	fieldStyle?: React.CSSProperties;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Select field for the Permission model.
 */
export function Select<FV extends FormValues>({
	formState,
	name,
	currentInstance,
	display = displayPermission,
	queryOptions,
	load,
	fieldClassName,
	fieldStyle,
	className,
	style,
	...props
}: SelectProps<FV>) {
	const defaultOrder = IndexForms.useOrderInput(PermissionOrderFormValues.initial);
	const [loadOptions, { loading, data, refetch }] = usePermissionSelectLazyQuery({
		...queryOptions,
		variables: {
			...queryOptions?.variables,
			order: queryOptions?.variables?.order ?? defaultOrder,
		},
		notifyOnNetworkStatusChange: true,
	});
	const isEditing = formState.formEditing[name];
	React.useEffect(() => {
		if (load || (load !== false && isEditing)) {
			loadOptions();
		}
	}, [load, isEditing, loadOptions]);
	const options = React.useMemo(
		() => PermissionSelect.toOptions(data?.permissionConnection.edges, currentInstance),
		[data, currentInstance],
	);
	const refetchOptions = React.useCallback(() => refetch(), [refetch]);

	return (
		<Column
			justify="spaced-start"
			align="flex-end"
			verticalSpacing="0.25rem"
			className={makeClassName(styles["select"], className)}
			style={style}
		>
			<SelectField
				formState={formState}
				name={name}
				isLoading={loading}
				options={options}
				display={display}
				blankValue={null}
				refetchOptions={refetchOptions}
				className={makeClassName(styles["select__field"], fieldClassName)}
				style={fieldStyle}
				{...props}
			/>
		</Column>
	);
}

export type MultiSelectProps<FV extends FormValues> = Omit<
	MultiSelectFieldProps<FV, Permission["id"]>,
	"options" | "isLoading" | "className" | "style"
> & {
	currentInstances?: PermissionSelect.ModelForOption[];
	queryOptions?: LazyQueryHookOptions<PermissionSelectQuery, PermissionSelectQueryVariables>;
	load?: boolean;
	fieldClassName?: string;
	fieldStyle?: React.CSSProperties;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Multi-select field for the Permission model.
 */
export function MultiSelect<FV extends FormValues>({
	formState,
	name,
	currentInstances,
	displayInstance = displayPermission,
	queryOptions,
	load,
	fieldClassName,
	fieldStyle,
	className,
	style,
	...props
}: MultiSelectProps<FV>) {
	const defaultOrder = IndexForms.useOrderInput(PermissionOrderFormValues.initial);
	const [loadOptions, { loading, data, refetch }] = usePermissionSelectLazyQuery({
		...queryOptions,
		variables: {
			...queryOptions?.variables,
			order: queryOptions?.variables?.order ?? defaultOrder,
		},
		notifyOnNetworkStatusChange: true,
	});
	const isEditing = formState.formEditing[name];
	React.useEffect(() => {
		if (load || (load !== false && isEditing)) {
			loadOptions();
		}
	}, [load, isEditing, loadOptions]);
	const options = React.useMemo(
		() => PermissionSelect.toMultiOptions(data?.permissionConnection.edges, currentInstances),
		[data, currentInstances],
	);
	const refetchOptions = React.useCallback(() => refetch(), [refetch]);

	return (
		<Column
			justify="spaced-start"
			align="flex-end"
			verticalSpacing="0.25rem"
			className={makeClassName(styles["multi-select"], className)}
			style={style}
		>
			<MultiSelectField
				formState={formState}
				name={name}
				isLoading={loading}
				options={options}
				displayInstance={displayInstance}
				refetchOptions={refetchOptions}
				className={makeClassName(styles["multi-select__field"], fieldClassName)}
				style={fieldStyle}
				{...props}
			/>
		</Column>
	);
}

function displayPermission({ value: id, formattedValue }: FieldDisplayArgs<string | null>) {
	if (id === null) {
		return formattedValue;
	}
	return <PermissionLink instance={{ id }}>{formattedValue}</PermissionLink>;
}
